import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService, LoginOptions } from '../services/authService';
import { useToast } from '@chakra-ui/react';
import DocumentThreadService from '../services/documentThreadService';

interface User {
  id: string;
  username: string;
  email?: string;
  isAdmin?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string, options?: LoginOptions) => Promise<void>;
  logout: () => void;
  checkAuthStatus: () => Promise<void>;
  hasRememberMe: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasRememberMe, setHasRememberMe] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const checkAuthStatus = async () => {
    try {
      // First try to restore from cookies if no localStorage token
      if (!localStorage.getItem('authToken')) {
        const restored = AuthService.restoreFromCookies();
        if (restored) {
          console.log('Successfully restored authentication from cookies');
        }
      }
      
      const token = AuthService.getToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        // Try to verify token with backend
        const userData = await AuthService.verifyToken();
        setUser(userData);
        setHasRememberMe(AuthService.hasRememberMe());
        console.log('Authentication status verified successfully');
      } catch (verifyError: any) {
        console.warn('Token verification failed, but token exists:', verifyError.message);
        
        // If we have a token but verification failed, check if it's a network issue
        // In this case, we'll try to use stored user data as a fallback
        const storedUser = AuthService.getStoredUser();
        if (storedUser && token) {
          console.log('Using stored user data as fallback');
          setUser(storedUser);
          setHasRememberMe(AuthService.hasRememberMe());
        } else {
          // No stored user data, clear everything
          console.log('No stored user data available, clearing auth');
          AuthService.logout();
          setUser(null);
          setHasRememberMe(false);
        }
      }
    } catch (error: any) {
      console.error('Auth check failed:', error);
      // Only clear auth data if it's a critical error, not a network issue
      if (error.message.includes('Token expired') || error.message.includes('No token found')) {
        AuthService.logout();
        setUser(null);
        setHasRememberMe(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username: string, password: string, options: LoginOptions = {}) => {
    try {
      setIsLoading(true);
      const response = await AuthService.login(username, password, options);
      
      // Verify token with backend to get actual user data
      const userData = await AuthService.verifyToken();
      setUser(userData);
      setHasRememberMe(!!options.rememberMe);
      
      // Start a new session to clear all previous chat threads
      console.log('🔄 Starting new session for user:', userData.username);
      const threadService = DocumentThreadService.getInstance();
      threadService.startNewSession();
      
      const welcomeMessage = options.rememberMe 
        ? `Welcome back, ${userData.username}! We'll remember you for 30 days.`
        : `Welcome back, ${userData.username}!`;
      
      toast({
        title: 'Login successful',
        description: welcomeMessage,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      
      // Navigate to returnUrl if provided, otherwise go to home
      const returnUrl = options.returnUrl || '/';
      navigate(returnUrl, { replace: true });
    } catch (error: any) {
      console.error('Login failed:', error);
      toast({
        title: 'Login failed',
        description: error.message || 'Please check your credentials and try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    AuthService.logout(); // This clears both localStorage and cookies
    setUser(null);
    setHasRememberMe(false);
    navigate('/auth/login');
    
    toast({
      title: 'Logged out',
      description: 'You have been successfully logged out.',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Add periodic authentication check for "Remember me" functionality
  useEffect(() => {
    if (user && hasRememberMe) {
      // Check auth status every 5 minutes when "Remember me" is enabled
      const interval = setInterval(async () => {
        console.log('Periodic auth check for "Remember me" user');
        
        // Check if token is expiring soon
        if (AuthService.isTokenExpiringSoon()) {
          console.log('Token expiring soon, attempting to refresh...');
          try {
            await checkAuthStatus();
          } catch (error) {
            console.warn('Failed to refresh token during periodic check:', error);
          }
        } else {
          // Just do a basic check without backend verification
          const token = AuthService.getToken();
          if (!token || AuthService.isTokenExpired()) {
            console.log('Token invalid during periodic check, clearing auth');
            AuthService.logout();
            setUser(null);
            setHasRememberMe(false);
          }
        }
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearInterval(interval);
    }
  }, [user, hasRememberMe]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkAuthStatus,
    hasRememberMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};